import React from 'react';
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useChartColors, money, bucketLabel } from './theme';

// One series over time: an area in a single hue. No legend, because the panel title
// already names the only thing on the chart, and no dot on every point.
export default function RevenueTrend({ series = [], bucket }) {
  const c = useChartColors();
  const data = series.map((b) => ({ ...b, label: bucketLabel(b.bucket) }));
  const empty = data.every((b) => !b.revenue_cents);

  return (
    <section className="border border-gray-200 dark:border-gray-700 rounded-xl p-4" aria-label="Revenue over time">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-semibold">Revenue over time</h3>
        <span className="text-xs text-gray-500">by {bucket === 'day' ? 'day' : 'month'} ordered</span>
      </div>
      {empty ? (
        <div className="h-[220px] flex items-center justify-center text-xs text-gray-500">
          No revenue in this period.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c.series} stopOpacity={0.35} />
                <stop offset="100%" stopColor={c.series} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            {/* Horizontal rules only: vertical ones fight the area's own shape. */}
            <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: c.axis }} tickLine={false}
                   axisLine={{ stroke: c.grid }} minTickGap={24} />
            <YAxis tick={{ fontSize: 11, fill: c.axis }} tickLine={false} axisLine={false}
                   width={64} tickFormatter={(v) => money(v)} />
            <Tooltip
              cursor={{ stroke: c.axis, strokeDasharray: '3 3' }}
              contentStyle={{ fontSize: 12, borderRadius: 12, border: `1px solid ${c.grid}`,
                              background: c.surface, color: c.axis }}
              formatter={(value, name) => (name === 'revenue_cents' ? [money(value), 'Revenue'] : [value, name])}
              labelFormatter={(l, payload) => {
                const p = payload?.[0]?.payload;
                return p ? `${l} · ${p.orders} order${p.orders === 1 ? '' : 's'} · ${p.seats} seats` : l;
              }}
            />
            {/* No mount animation: the chart re-renders on every filter change, so a
                grow-in would turn routine filtering into flicker. It also makes the
                first paint deterministic. */}
            <Area type="monotone" dataKey="revenue_cents" stroke={c.series} strokeWidth={2}
                  fill="url(#revFill)" isAnimationActive={false}
                  activeDot={{ r: 4, strokeWidth: 2, stroke: c.surface }} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </section>
  );
}
